import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass, AggridSwitchButton, aggridSwitchButtonWrapperStyle } from "src/components/common/common";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { defaultSYS3060_R01Dto, SYS3060_R01Dto } from "src/containers/mes/SYS/SYS3060_R01/Dto_SYS3060_R01";
import { useSYS3060_R01Store } from "src/containers/mes/SYS/SYS3060_R01/Store_SYS3060_R01";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, GridCellConfig, RemoveRowHandler, createCellCssClassRules, getSwitchButtonColumnOptions, postSortHandler, useCellChangeHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags,  } from "src/utils/grid/gridStyleUtils";
import { CopySearchSection_SYS3060_R01 } from "./CopySearchSection_SYS3060_R01";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";

const COLUMN_STATE_KEY = 'SYS3060_R01-menu-authority-grid-column-state';

interface Props {
	selectedSourceAutGroup: string;
	setSelectedSourceAutGroup: (value: string) => void;
	selectedTargetAutGroup: string;
	setSelectedTargetAutGroup: (value: string) => void;
	handleCopyClick: () => void;
	onCopyComplete?: () => void;
	defaultSYS3060_R01DetailRow: () => SYS3060_R01Dto;
	gridRef: React.RefObject<AgGridReact<SYS3060_R01Dto> | null>;
}

export const DetailTable_SYS3060_R01 = (props: Props) => {
	const {
		selectedSourceAutGroup, setSelectedSourceAutGroup,
		selectedTargetAutGroup, setSelectedTargetAutGroup,
		handleCopyClick, onCopyComplete,
		defaultSYS3060_R01DetailRow, gridRef,
	} = props;

	const {
		SYS3060_R01List,
		menuAuthorityList, setMenuAuthorityList,
		updateMenuAuthorityItem,
		deletedMenuAuthorityList,
		selectedMenuAuthorityList, addSelectedMenuAuthorityList, removeSelectedMenuAuthorityList,
		initSelectedMenuAuthorityList, allSelectedMenuAuthorityList,
		originalMenuAuthorityList,
		menuAuthorityColumnState, setMenuAuthorityColumnState,
		detailScrollTop, setDetailScrollTop,
	} = useSYS3060_R01Store();

	const [clickable, setClickable] = useState(true);
	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
		handleSwitchButtonHandler,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: menuAuthorityList,
		defaultGridData: defaultSYS3060_R01DetailRow(),
		selectedRow: selectedMenuAuthorityList,
		originalGridData: originalMenuAuthorityList,
		updateRow: (index: number, data: any) => updateMenuAuthorityItem(index, data),
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		menuCd: '메뉴코드',
		menuNm: '메뉴명',
		useYn: '사용여부',
		queryAut: '조회',
		insertAut: '등록',
		deleteAut: '삭제',
		saveAut: '저장',
		printAut: '출력',
		excelAut: '엑셀',
		remark: '비고',
	};

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['menuCd', 'menuNm', 'useYn'],
		insertedDisabledCells: [''],
		commonDisabledCells: [''],
		
		pointerCells: ['queryAut', 'insertAut', 'deleteAut', 'saveAut', 'printAut', 'excelAut', 'remark'],
		insertedPointerCells: [''],
		commonPointerCells: [''],
		
		alignLeftCells: ['']
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<SYS3060_R01Dto>({
			idKey: 'valueIndex',
			selectedList: selectedMenuAuthorityList,
			deletedList: deletedMenuAuthorityList,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules, }};
	}, [selectedMenuAuthorityList, deletedMenuAuthorityList, disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns, createCellClassRules]);

	const baseColumnDefs: ColDef<SYS3060_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			(params: SYS3060_R01Dto) => renderIndexCell(
				params, 
				(innerParams: SYS3060_R01Dto) => getRowStyleFlags (
					innerParams,
					{
						deletedList: deletedMenuAuthorityList,
						selectedList: selectedMenuAuthorityList,
						dataList: menuAuthorityList
					}),
				() => {},
				() => {},
			)
		),
		{
			headerName: "메뉴코드", colId: "menuCd", field: "menuCd", width: 100,
			editable: false,
		},
		{
			headerName: "메뉴명", colId: "menuNm", field: "menuNm", width: 180,
			editable: false,
		},
		{
			headerName: "사용여부", colId: "useYn", field: "useYn", width: 80,
			...getSwitchButtonColumnOptions('useYn', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, false),
		},
		{
			headerName: "조회", colId: "queryAut", field: "queryAut", width: 70,
			...getSwitchButtonColumnOptions('queryAut', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
		},
		{
			headerName: "등록", colId: "insertAut", field: "insertAut", width: 70,
			...getSwitchButtonColumnOptions('insertAut', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
		},
		{
			headerName: "삭제", colId: "deleteAut", field: "deleteAut", width: 70,
			...getSwitchButtonColumnOptions('deleteAut', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
		},
		{
			headerName: "저장", colId: "saveAut", field: "saveAut", width: 70,
			...getSwitchButtonColumnOptions('saveAut', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
		},
		{
			headerName: "출력", colId: "printAut", field: "printAut", width: 70,
			...getSwitchButtonColumnOptions('printAut', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
		},
		{
			headerName: "엑셀", colId: "excelAut", field: "excelAut", width: 70,
			...getSwitchButtonColumnOptions('excelAut', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
		},
		{
			headerName: "비고", colId: "remark", field: "remark", width: 120,
			editable: true,
		},
		// ...IndexParameterCell(),
	], [selectedMenuAuthorityList, menuAuthorityList, originalMenuAuthorityList, createCellClassRules]);

	
	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs: ColDef<SYS3060_R01Dto>[] = useMemo(() => {
		if (menuAuthorityColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<SYS3060_R01Dto>({
			baseColumnDefs,
			columnState: menuAuthorityColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, menuAuthorityColumnState, headerNames, originalMenuAuthorityList]);

    const handleRowClick = useRowSelectionHandler({
        dataList: menuAuthorityList,
        idKey: 'valueIndex',
        selectedList: selectedMenuAuthorityList,
        deletedList: deletedMenuAuthorityList,
        addSelected: addSelectedMenuAuthorityList,
        removeSelected: removeSelectedMenuAuthorityList,
        clickable, setClickable
    });

	const handleCellChange = useCellChangeHandler({
        dataList: menuAuthorityList,
        originalDataList: originalMenuAuthorityList,
        updateRow: updateMenuAuthorityItem,
    });

	// 컬럼 클릭 액션 설정, 모달 이 있다면 모달 켜는 함수도 의존성 설정을 해줘야 한다
	const commonInputColumns = [ 'remark',];
	const cellClickActions: CustomCellClickAction<SYS3060_R01Dto>[] = useMemo(() => [
		{
			validate: (field, data) => commonInputColumns.includes(field),
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], []);
	const handleCellClick = CreateCellClickHandler(menuAuthorityList, 'valueIndex', cellClickActions);

	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: menuAuthorityList as any,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});

	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<SYS3060_R01Dto>[] = useMemo(() => [], []);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(menuAuthorityList, 'valueIndex', cellDoubleClickActions);

	
	const handlePostSort = useCallback((params: PostSortRowsParams<SYS3060_R01Dto>) => {
		postSortHandler(params, menuAuthorityList, originalMenuAuthorityList, setMenuAuthorityList);
	}, [menuAuthorityList, originalMenuAuthorityList]);

	// 하단 합계(메뉴 권한 수)
	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		menuCd: menuAuthorityList.length.toLocaleString() + "건",
		editable: false,
	}]), [menuAuthorityList.length]);

	return (
		<GridContextWrapper<SYS3060_R01Dto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			//setPaddedGridData={setPaddedGridData}
			initSelected={initSelectedMenuAuthorityList}
			defaultRowData={defaultSYS3060_R01DetailRow()}
			allSelected={allSelectedMenuAuthorityList}
			baseColumnDefs={menuAuthorityColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setMenuAuthorityColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<CopySearchSection_SYS3060_R01
				autGroupOptions={SYS3060_R01List}
				selectedSourceAutGroup={selectedSourceAutGroup}
				setSelectedSourceAutGroup={setSelectedSourceAutGroup}
				selectedTargetAutGroup={selectedTargetAutGroup}
				setSelectedTargetAutGroup={setSelectedTargetAutGroup}
				handleCopyClick={handleCopyClick}
				onCopyComplete={onCopyComplete}
			/>
				<Grid_Excel<SYS3060_R01Dto>
					ref={gridRef}
					rowData={paddedGridData}	getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
					columnDefs={columnDefs}		
					defaultColDef={cellCssDefaultColDef}

					rowClassRules={createRowClassRules<SYS3060_R01Dto>({
						idKey: 'valueIndex',
						selectedList: selectedMenuAuthorityList,
						deletedList: deletedMenuAuthorityList
					})}
					onRowClicked={handleRowClick}
					onCellClicked={handleCellClick}
					onCellDoubleClicked={handleCellDoubleClick}
					onCellKeyDown={cellEventHandlers.handleCellKeyDown}
					onCellValueChanged={handleCellChange}
					pinnedBottomRowData={pinnedBottomRow}

					onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
					// Grid_Select에 공통 로직 처리를 위한 props 전달
					columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
					setColumnState={setMenuAuthorityColumnState}				//컬럼 상태 저장
					getDefaultColumnState={() => defaultColumnState}
					scrollTop={detailScrollTop}
					setScrollTop={setDetailScrollTop}
					
				/>
			</GridContextWrapper>
	);
};

export default DetailTable_SYS3060_R01; 
